import { forwardRef, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { parseFramework, fmtInch } from "@/lib/svgUtils";

const SVGNS = "http://www.w3.org/2000/svg";

// Block Chrome text-selection / native drag from hijacking an active gesture.
const blockNative = (e) => e.preventDefault();
const clearSelection = () => { try { window.getSelection()?.removeAllRanges(); } catch (err) { /* noop */ } };
const NO_SELECT = { userSelect: "none", WebkitUserSelect: "none", WebkitTouchCallout: "none" };

function rotatePoint(px, py, cx, cy, deg) {
  const r = (-deg * Math.PI) / 180;
  const dx = px - cx;
  const dy = py - cy;
  return {
    x: cx + dx * Math.cos(r) - dy * Math.sin(r),
    y: cy + dx * Math.sin(r) + dy * Math.cos(r),
  };
}

const StudioCanvas = forwardRef(function StudioCanvas(
  { svgString, graphics, onChange, selectedId, setSelectedId, activePartId, setActivePartId, editable = true, uniformScale = true, partColors = {}, guideColor = "#111111", zoom = 1, onZoom, onCalibrate, onParts, unit = "in", lockedPartIds = [], className = "", "data-testid": dataTestid },
  ref
) {
  const { viewBox, inner, guides, measurement } = useMemo(() => parseFramework(svgString), [svgString]);
  const vb = useMemo(() => {
    const p = (viewBox || "0 0 800 600").split(/[\s,]+/).map(Number);
    return { x: p[0] || 0, y: p[1] || 0, w: p[2] || 800, h: p[3] || 600 };
  }, [viewBox]);
  const svgRef = useRef(null);
  const viewportRef = useRef(null);
  const wrapperRef = useRef(null);
  const defsRef = useRef(null);
  const bboxRef = useRef({});
  const origFillRef = useRef({});
  const guideRef = useRef(null);
  const measureRef = useRef(null);
  const [upi, setUpi] = useState(null);
  // Viewport metrics for CSS-transform pan/zoom (keeps the SVG viewBox fixed so the
  // canvas rasterizes ONCE and pan/zoom are GPU-composited — no per-frame re-raster,
  // which is what crashed Android Chrome's renderer).
  const [metrics, setMetrics] = useState({ W: 1, H: 1, k: 1, ox: 0, oy: 0 });
  const metricsRef = useRef(metrics);
  metricsRef.current = metrics;
  // Cursor-anchored dimension badge, active only during a resize/scale drag.
  const [sizing, setSizing] = useState(null);
  const [coarse] = useState(() => typeof window !== "undefined" && !!window.matchMedia && window.matchMedia("(pointer: coarse)").matches);
  const uniformRef = useRef(uniformScale);
  uniformRef.current = uniformScale;
  const opRef = useRef(null);
  const activeListenersRef = useRef(null);
  // Live manipulation preview: during an active drag/resize/rotate we apply a GPU
  // transform to the existing raster instead of rewriting the <image> width/height
  // every frame (which forces Android Chrome to re-rasterize and can crash the tab).
  const [preview, setPreview] = useState(null);
  const moveRafRef = useRef(0);
  const movePendingRef = useRef(null);
  const graphicsRef = useRef(graphics);
  graphicsRef.current = graphics;

  // Build clip paths + measure part bounding boxes after the framework renders.
  useEffect(() => {
    const svg = svgRef.current;
    const defs = defsRef.current;
    if (!svg || !defs) return;
    defs.innerHTML = "";
    const boxes = {};
    const rootCTM = svg.getScreenCTM();
    const toRootBBox = (el) => {
      const bb = el.getBBox();
      const elCTM = el.getScreenCTM();
      if (!rootCTM || !elCTM) return { x: bb.x, y: bb.y, width: bb.width, height: bb.height };
      const m = rootCTM.inverse().multiply(elCTM); // element local -> svg root user space
      const corners = [
        [bb.x, bb.y],
        [bb.x + bb.width, bb.y],
        [bb.x, bb.y + bb.height],
        [bb.x + bb.width, bb.y + bb.height],
      ].map(([x, y]) => {
        const pt = svg.createSVGPoint();
        pt.x = x; pt.y = y;
        return pt.matrixTransform(m);
      });
      const xs = corners.map((p) => p.x);
      const ys = corners.map((p) => p.y);
      const minX = Math.min(...xs), minY = Math.min(...ys);
      return { x: minX, y: minY, width: Math.max(...xs) - minX, height: Math.max(...ys) - minY };
    };
    svg.querySelectorAll('[id]').forEach((el) => {
      const id = el.getAttribute("id");
      if (!id || !id.toLowerCase().startsWith("part")) return;
      if (origFillRef.current[id] === undefined) {
        origFillRef.current[id] = el.getAttribute("fill") || "";
      }
      try {
        const box = toRootBBox(el);
        if (!(box.width > 0 && box.height > 0)) return;
        boxes[id] = box;
      } catch (e) {
        return;
      }
      const clip = document.createElementNS(SVGNS, "clipPath");
      clip.setAttribute("id", `clip-${id}`);
      clip.setAttribute("clipPathUnits", "userSpaceOnUse");
      const clone = el.cloneNode(true);
      clone.removeAttribute("id");
      clone.removeAttribute("fill");
      clone.removeAttribute("stroke");
      clone.removeAttribute("stroke-width");
      clone.removeAttribute("style");
      clip.appendChild(clone);
      defs.appendChild(clip);
    });
    bboxRef.current = boxes;
    if (onParts) onParts(boxes);

    // Calibrate pixels->inches from the hidden 1x1 inch "measurement" reference shape.
    let scale = null;
    const mEl = measureRef.current;
    if (mEl && mEl.getBBox) {
      try {
        const mb = toRootBBox(mEl);
        if (mb.width > 0 && mb.height > 0) scale = (mb.width + mb.height) / 2;
      } catch (e) { scale = null; }
    }
    setUpi(scale);
    if (onCalibrate) onCalibrate(scale);
  }, [svgString]);

  // Apply solid block colors to parts (declarative overlay rects rendered below graphics).

  // Recolor the guide overlay (Red / Black / White).
  useEffect(() => {
    const g = guideRef.current;
    if (!g) return;
    g.querySelectorAll("*").forEach((el) => {
      const of = el.getAttribute("fill");
      const os = el.getAttribute("stroke");
      const hasFill = of && of !== "none";
      const hasStroke = os && os !== "none";
      if (hasStroke) el.style.stroke = guideColor;
      if (hasFill) el.style.fill = guideColor;
      if (!hasFill && !hasStroke) el.style.stroke = guideColor;
    });
  }, [guideColor, guides]);

  // Highlight the active part region.
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    svg.querySelectorAll('[id]').forEach((el) => {
      const id = el.getAttribute("id");
      if (!id || !id.toLowerCase().startsWith("part")) return;
      if (editable && id === activePartId) {
        el.setAttribute("stroke", "#C85A32");
        el.setAttribute("stroke-width", "3");
      } else {
        el.setAttribute("stroke", "#B7A888");
        el.setAttribute("stroke-width", "1.5");
      }
    });
  }, [activePartId, editable, svgString]);

  const toSvgPoint = (clientX, clientY) => {
    const svg = svgRef.current;
    if (!svg) return { x: 0, y: 0 };
    const ctm = svg.getScreenCTM();
    if (!ctm) return { x: 0, y: 0 };
    const pt = svg.createSVGPoint();
    pt.x = clientX;
    pt.y = clientY;
    return pt.matrixTransform(ctm.inverse());
  };

  // ---- Zoom & pan (implemented by adjusting the viewBox so pointer math stays correct) ----
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const panRef = useRef(pan);
  panRef.current = pan;
  const panOpRef = useRef(null);
  const panRafRef = useRef(0);
  const panPendingRef = useRef(null);
  const panMovedRef = useRef(false);
  const opEndedAtRef = useRef(0);
  const pointersRef = useRef(new Map());
  const pinchRef = useRef(null);
  const pinchRafRef = useRef(0);
  const pinchPendingRef = useRef(null);
  // Pinch-to-RESIZE the selected graphic (distinct from pinch-to-zoom the canvas).
  const selectedIdRef = useRef(selectedId);
  selectedIdRef.current = selectedId;
  const zoomRef = useRef(zoom);
  zoomRef.current = zoom;
  const livePtrsRef = useRef(new Map());
  const gfxPinchRef = useRef(null);
  const gfxPinchRafRef = useRef(0);
  const gfxPinchPendingRef = useRef(null);
  const prevZoomRef = useRef(zoom);
  const zoomAnchorRef = useRef(null); // cursor content-point for wheel zoom; null => center-anchored

  const clampPan = (p, z) => {
    const sliceW = vb.w / z, sliceH = vb.h / z;
    // Allow only a small overscroll beyond the artwork edges so the SVG can never be
    // dragged (mostly) off-screen or drift into empty space — it stays reachable.
    const marginX = sliceW * 0.15, marginY = sliceH * 0.15;
    return {
      x: Math.min(Math.max(p.x, -marginX), (vb.w - sliceW) + marginX),
      y: Math.min(Math.max(p.y, -marginY), (vb.h - sliceH) + marginY),
    };
  };

  // Re-anchor pan whenever zoom changes so the focal point stays put:
  //  - buttons (no cursor anchor) keep the viewport CENTER fixed
  //  - mouse wheel keeps the point under the CURSOR fixed
  useEffect(() => {
    const z0 = prevZoomRef.current || 1;
    const z1 = zoom;
    setPan((p) => {
      if (z1 === 1) return { x: 0, y: 0 };
      const anchor = zoomAnchorRef.current;
      if (anchor) {
        const fx = (anchor.x - vb.x - p.x) / (vb.w / z0);
        const fy = (anchor.y - vb.y - p.y) / (vb.h / z0);
        return clampPan(
          { x: anchor.x - vb.x - fx * (vb.w / z1), y: anchor.y - vb.y - fy * (vb.h / z1) },
          z1
        );
      }
      return clampPan(
        { x: p.x + (vb.w / 2) * (1 / z0 - 1 / z1), y: p.y + (vb.h / 2) * (1 / z0 - 1 / z1) },
        z1
      );
    });
    zoomAnchorRef.current = null;
    prevZoomRef.current = z1;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoom, svgString]);

  // Measure the viewport (unscaled container) and derive the base user->px scale.
  useLayoutEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const measure = () => {
      const W = el.clientWidth || 1, H = el.clientHeight || 1;
      const k = Math.min(W / vb.w, H / vb.h) || 1;
      setMetrics({ W, H, k, ox: (W - vb.w * k) / 2, oy: (H - vb.h * k) / 2 });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [vb.w, vb.h]);

  // Mouse-wheel zoom (native non-passive listener so preventDefault stops page scroll).
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg || !onZoom) return;
    const onWheel = (e) => {
      e.preventDefault();
      const dir = e.deltaY < 0 ? 1 : -1;
      const p = toSvgPoint(e.clientX, e.clientY);
      zoomAnchorRef.current = { x: p.x, y: p.y };
      onZoom((z) => Math.min(4, Math.max(1, +(z + dir * 0.25).toFixed(2))));
    };
    svg.addEventListener("wheel", onWheel, { passive: false });
    return () => svg.removeEventListener("wheel", onWheel);
  }, [onZoom]);

  // Always drop a pointer from tracking when it lifts, so a stale pointer can't
  // turn a later single-finger pan into a false pinch.
  useEffect(() => {
    const drop = (e) => { pointersRef.current.delete(e.pointerId); };
    window.addEventListener("pointerup", drop);
    window.addEventListener("pointercancel", drop);
    return () => { window.removeEventListener("pointerup", drop); window.removeEventListener("pointercancel", drop); };
  }, []);

  // Pinch-to-resize the SELECTED graphic. Uses capture-phase pointer tracking so it
  // counts fingers even on the graphic (whose own handler stops propagation). Two
  // touch points while a graphic is selected scale it proportionally about its centre;
  // starting a pinch cancels any in-progress single-finger drag/pan so they don't fight.
  useEffect(() => {
    if (!editable) return;
    const pts = () => [...livePtrsRef.current.values()];
    const beginPinch = () => {
      const p = pts();
      if (!(selectedIdRef.current && p.length >= 2 && !gfxPinchRef.current)) return;
      const g = graphicsRef.current.find((x) => x.id === selectedIdRef.current);
      if (!g || g.dup) return;
      // Fold any uncommitted single-finger gesture (preview-only pan/resize/rotate)
      // into the start state so the pinch begins exactly where the graphic currently
      // appears — otherwise it snaps back to the pre-gesture committed position.
      const op = opRef.current;
      const r = op && op.id === g.id ? op.result : null;
      const startCx = r && r.cx != null ? r.cx : g.cx;
      const startCy = r && r.cy != null ? r.cy : g.cy;
      const startW = r && r.w != null ? r.w : g.w;
      const startH = r && r.h != null ? r.h : g.h;
      const startRot = r && r.rotation != null ? r.rotation : (g.rotation || 0);
      if (r) update(g.id, r); // commit it so React state matches the folded start
      const dist = Math.hypot(p[0].x - p[1].x, p[0].y - p[1].y) || 1;
      const ang = Math.atan2(p[1].y - p[0].y, p[1].x - p[0].x);
      const mid = toSvgPoint((p[0].x + p[1].x) / 2, (p[0].y + p[1].y) / 2);
      gfxPinchRef.current = {
        id: g.id, startDist: dist, startAngle: ang,
        startMidX: mid.x, startMidY: mid.y,
        startW, startH, startCx, startCy, startRot,
      };
      // Stop any single-finger manipulation and fully cancel any stray pan,
      // including the imperative wrapper transform it may have already applied.
      opRef.current = null; setPreview(null); setSizing(null); detachWindow();
      // Kill any pending single-finger-move frame so a stale drag transform can't
      // re-apply as preview after the pinch takes over (caused post-pinch snapping).
      if (moveRafRef.current) { cancelAnimationFrame(moveRafRef.current); moveRafRef.current = 0; }
      movePendingRef.current = null;
      panPendingRef.current = null; onPanUp();
      if (wrapperRef.current) {
        const m = metricsRef.current, z = zoomRef.current;
        const Tx = m.ox * (1 - z) - panRef.current.x * m.k * z;
        const Ty = m.oy * (1 - z) - panRef.current.y * m.k * z;
        wrapperRef.current.style.transform = `translate(${Tx}px, ${Ty}px) scale(${z})`;
      }
      pinchRef.current = null; pointersRef.current.clear();
    };
    const pd = (e) => {
      if (e.pointerType !== "touch") return;
      livePtrsRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
      beginPinch();
    };
    const pm = (e) => {
      if (!livePtrsRef.current.has(e.pointerId)) return;
      livePtrsRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
      beginPinch(); // lazily start if the pointerdown-time start was missed
      const gp = gfxPinchRef.current;
      if (!gp) return;
      const p = pts();
      if (p.length < 2) return;
      if (e.cancelable) e.preventDefault();
      // Decoupled two-finger transform: midpoint shift -> pan, distance ratio ->
      // scale, angle delta -> rotate. All three apply together about the fingers,
      // so neither finger fights the other and the centre never jumps.
      const dist = Math.hypot(p[0].x - p[1].x, p[0].y - p[1].y) || 1;
      const ang = Math.atan2(p[1].y - p[0].y, p[1].x - p[0].x);
      const mid = toSvgPoint((p[0].x + p[1].x) / 2, (p[0].y + p[1].y) / 2);
      const f = dist / gp.startDist;
      const dDeg = ((ang - gp.startAngle) * 180) / Math.PI;
      gfxPinchPendingRef.current = {
        w: Math.max(16, gp.startW * f),
        h: Math.max(16, gp.startH * f),
        cx: gp.startCx + (mid.x - gp.startMidX),
        cy: gp.startCy + (mid.y - gp.startMidY),
        rotation: snapRotation(gp.startRot + dDeg),
      };
      if (!gfxPinchRafRef.current) {
        gfxPinchRafRef.current = requestAnimationFrame(() => {
          gfxPinchRafRef.current = 0;
          const patch = gfxPinchPendingRef.current;
          if (patch && gfxPinchRef.current) update(gfxPinchRef.current.id, patch);
        });
      }
    };
    const pu = (e) => {
      livePtrsRef.current.delete(e.pointerId);
      if (gfxPinchRef.current && livePtrsRef.current.size < 2) {
        gfxPinchRef.current = null;
        if (gfxPinchRafRef.current) { cancelAnimationFrame(gfxPinchRafRef.current); gfxPinchRafRef.current = 0; }
      }
    };
    window.addEventListener("pointerdown", pd, true);
    window.addEventListener("pointermove", pm, { capture: true, passive: false });
    window.addEventListener("pointerup", pu, true);
    window.addEventListener("pointercancel", pu, true);
    return () => {
      window.removeEventListener("pointerdown", pd, true);
      window.removeEventListener("pointermove", pm, true);
      window.removeEventListener("pointerup", pu, true);
      window.removeEventListener("pointercancel", pu, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editable]);

  const startPan = (e) => {
    panOpRef.current = { sx: e.clientX, sy: e.clientY, px: panRef.current.x, py: panRef.current.y };
    panMovedRef.current = false;
    window.addEventListener("pointermove", onPanMove);
    window.addEventListener("pointerup", onPanUp);
    window.addEventListener("pointercancel", onPanUp);
  };
  const onPanMove = (e) => {
    if (gfxPinchRef.current) return;
    const op = panOpRef.current;
    if (!op) return;
    const dx = e.clientX - op.sx;
    const dy = e.clientY - op.sy;
    if (Math.abs(dx) + Math.abs(dy) > 3) panMovedRef.current = true;
    const s = (metricsRef.current.k * zoom) || 1; // user units per screen px (inverse)
    panPendingRef.current = clampPan({ x: op.px - dx / s, y: op.py - dy / s }, zoom);
    // Apply the pan transform DIRECTLY to the DOM — no React re-render, so the heavy
    // template layer is only composited (translated), never re-rendered/repainted.
    if (!panRafRef.current) {
      panRafRef.current = requestAnimationFrame(() => {
        panRafRef.current = 0;
        const p = panPendingRef.current;
        if (p && wrapperRef.current) {
          const m = metricsRef.current;
          const Tx = m.ox * (1 - zoom) - p.x * m.k * zoom;
          const Ty = m.oy * (1 - zoom) - p.y * m.k * zoom;
          wrapperRef.current.style.transform = `translate(${Tx}px, ${Ty}px) scale(${zoom})`;
        }
      });
    }
  };
  const onPanUp = () => {
    panOpRef.current = null;
    if (panRafRef.current) { cancelAnimationFrame(panRafRef.current); panRafRef.current = 0; }
    if (panPendingRef.current) { setPan(panPendingRef.current); panPendingRef.current = null; }
    window.removeEventListener("pointermove", onPanMove);
    window.removeEventListener("pointerup", onPanUp);
    window.removeEventListener("pointercancel", onPanUp);
  };

  // ---- Pinch-to-zoom (two-finger). One-finger pan/drag remain unchanged. ----
  const onPinchMove = (e) => {
    if (!pointersRef.current.has(e.pointerId)) return;
    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const pts = [...pointersRef.current.values()];
    if (pts.length < 2 || !pinchRef.current || !onZoom) return;
    if (e.cancelable) e.preventDefault();
    const dist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y) || 1;
    const mid = toSvgPoint((pts[0].x + pts[1].x) / 2, (pts[0].y + pts[1].y) / 2);
    const nz = Math.min(4, Math.max(1, +(pinchRef.current.startZoom * (dist / pinchRef.current.startDist)).toFixed(3)));
    pinchPendingRef.current = { nz, ax: mid.x, ay: mid.y };
    if (!pinchRafRef.current) {
      pinchRafRef.current = requestAnimationFrame(() => {
        pinchRafRef.current = 0;
        const p = pinchPendingRef.current;
        if (p && onZoom) { zoomAnchorRef.current = { x: p.ax, y: p.ay }; onZoom(() => p.nz); }
      });
    }
  };

  const onPinchUp = (e) => {
    pointersRef.current.delete(e.pointerId);
    if (pointersRef.current.size < 2) {
      pinchRef.current = null;
      if (pinchRafRef.current) { cancelAnimationFrame(pinchRafRef.current); pinchRafRef.current = 0; }
      window.removeEventListener("pointermove", onPinchMove);
      window.removeEventListener("pointerup", onPinchUp);
      window.removeEventListener("pointercancel", onPinchUp);
    }
  };

  const onCanvasPointerDown = (e) => {
    if (gfxPinchRef.current) return; // a two-finger graphic resize is in progress
    clearSelection();
    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointersRef.current.size >= 2 && onZoom) {
      panPendingRef.current = null;
      onPanUp();            // a second finger cancels any in-progress pan (no drift commit)
      panMovedRef.current = true; // swallow the click that follows the gesture
      const pts = [...pointersRef.current.values()];
      const dist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y) || 1;
      pinchRef.current = { startDist: dist, startZoom: zoom };
      window.addEventListener("pointermove", onPinchMove);
      window.addEventListener("pointerup", onPinchUp);
      window.addEventListener("pointercancel", onPinchUp);
      return;
    }
    startPan(e);
  };

  const partUnderPoint = (clientX, clientY) => {
    const el = document.elementFromPoint(clientX, clientY);
    let node = el;
    while (node && node !== svgRef.current) {
      const id = node.getAttribute && node.getAttribute("id");
      if (id && id.toLowerCase().startsWith("part")) return id;
      node = node.parentNode;
    }
    return null;
  };

  const addGraphicToPart = (payload, partId) => {
    if (lockedPartIds.includes(partId)) return;
    const box = bboxRef.current[partId];
    const cx = box ? box.x + box.width / 2 : 400;
    const cy = box ? box.y + box.height / 2 : 300;
    const size = box ? Math.min(box.width, box.height) : 140;
    const g = {
      id: crypto.randomUUID(),
      fileId: payload.fileId,
      url: payload.url,
      filename: payload.filename,
      partId,
      cx,
      cy,
      originX: cx,
      originY: cy,
      w: size,
      h: size,
      rotation: 0,
      opacity: 1,
    };
    onChange([...graphicsRef.current, g]);
    setSelectedId && setSelectedId(g.id);
    fitToPart(g.id, payload.url, partId);
  };

  useImperativeHandle(ref, () => ({
    addGraphicToPart,
    getParts: () => bboxRef.current,
  }));

  const update = (id, patch) => {
    onChange(graphicsRef.current.map((g) => (g.id === id ? { ...g, ...patch } : g)));
  };

  // Scale a placed graphic to fully fit (contain) within its part mask, matching
  // the dimension that yields the smaller scale factor, then center it in the mask.
  const fitToPart = (id, url, partId) => {
    const box = bboxRef.current[partId];
    if (!url || !box) return;
    const img = new Image();
    img.onload = () => {
      const nw = img.naturalWidth || 1;
      const nh = img.naturalHeight || 1;
      const scale = Math.min(box.width / nw, box.height / nh);
      const w = nw * scale;
      const h = nh * scale;
      update(id, { w, h, cx: box.x + box.width / 2, cy: box.y + box.height / 2 });
    };
    img.src = url;
  };

  const snapRafRef = useRef(0);
  // A graphic is "fully outside" its part when its (rotation-aware) bounding box does
  // not intersect the part box at all. Any overlap (even 1px) keeps it in place — this
  // preserves intentional bleed off the edge for full-coverage printing.
  const isFullyOutside = (g, box) => {
    const r = ((g.rotation || 0) * Math.PI) / 180;
    const cos = Math.cos(r), sin = Math.sin(r);
    const hw = g.w / 2, hh = g.h / 2;
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const [dx, dy] of [[-hw, -hh], [hw, -hh], [hw, hh], [-hw, hh]]) {
      const x = g.cx + dx * cos - dy * sin;
      const y = g.cy + dx * sin + dy * cos;
      if (x < minX) minX = x; if (x > maxX) maxX = x;
      if (y < minY) minY = y; if (y > maxY) maxY = y;
    }
    return maxX <= box.x || minX >= box.x + box.width || maxY <= box.y || minY >= box.y + box.height;
  };
  // Ease the graphic's center back to the part center (easeOutCubic, ~180ms).
  const animateCenter = (id, tcx, tcy) => {
    if (snapRafRef.current) { cancelAnimationFrame(snapRafRef.current); snapRafRef.current = 0; }
    const g0 = graphicsRef.current.find((x) => x.id === id);
    if (!g0) return;
    const sx = g0.cx, sy = g0.cy, t0 = performance.now(), dur = 180;
    const step = (now) => {
      const t = Math.min(1, (now - t0) / dur);
      const e = 1 - Math.pow(1 - t, 3);
      update(id, { cx: sx + (tcx - sx) * e, cy: sy + (tcy - sy) * e });
      if (t < 1) snapRafRef.current = requestAnimationFrame(step);
      else snapRafRef.current = 0;
    };
    snapRafRef.current = requestAnimationFrame(step);
  };

  const startDrag = (e, g) => {
    if (!editable) return;
    if (gfxPinchRef.current || livePtrsRef.current.size >= 2) return;
    e.stopPropagation();
    if (e.cancelable) e.preventDefault();
    if (snapRafRef.current) { cancelAnimationFrame(snapRafRef.current); snapRafRef.current = 0; }
    setSelectedId && setSelectedId(g.id);
    const p = toSvgPoint(e.clientX, e.clientY);
    opRef.current = { type: "drag", id: g.id, offX: p.x - g.cx, offY: p.y - g.cy };
    attachWindow();
  };

  const startResize = (e, g, corner) => {
    if (gfxPinchRef.current || livePtrsRef.current.size >= 2) return;
    e.stopPropagation();
    if (e.cancelable) e.preventDefault();
    const p = toSvgPoint(e.clientX, e.clientY);
    const local = rotatePoint(p.x, p.y, g.cx, g.cy, g.rotation);
    const startDist = Math.hypot(local.x - g.cx, local.y - g.cy) || 1;
    opRef.current = { type: "resize", id: g.id, corner, startW: g.w, startH: g.h, startDist };
    setSizing({ x: e.clientX, y: e.clientY, w: g.w, h: g.h });
    attachWindow();
  };

  const startResizeVertical = (e, g) => {
    if (gfxPinchRef.current || livePtrsRef.current.size >= 2) return;
    e.stopPropagation();
    if (e.cancelable) e.preventDefault();
    opRef.current = { type: "resizeV", id: g.id, startW: g.w, startH: g.h };
    setSizing({ x: e.clientX, y: e.clientY, w: g.w, h: g.h });
    attachWindow();
  };

  const startRotate = (e, g) => {
    if (gfxPinchRef.current || livePtrsRef.current.size >= 2) return;
    e.stopPropagation();
    if (e.cancelable) e.preventDefault();
    opRef.current = { type: "rotate", id: g.id };
    attachWindow();
  };

  // Scale about a graphic's center within its own (pre-rotation) local axes.
  // Snap rotation to 0deg / 180deg when within a small threshold so graphics
  // align cleanly; otherwise keep the continuous (rounded) angle.
  const snapRotation = (deg) => {
    const T = 7;
    const r = ((deg % 360) + 360) % 360;
    if (r <= T || r >= 360 - T) return 0;
    if (Math.abs(r - 180) <= T) return 180;
    return Math.round(deg);
  };

  const scaleT = (g, sx, sy) =>
    `translate(${g.cx} ${g.cy}) rotate(${g.rotation}) scale(${sx} ${sy}) rotate(${-g.rotation}) translate(${-g.cx} ${-g.cy})`;

  // Coalesce pointermove updates to one visual update per animation frame.
  const flushMove = () => {
    moveRafRef.current = 0;
    if (gfxPinchRef.current) return; // a two-finger graphic transform owns the gesture
    const pend = movePendingRef.current;
    if (!pend) return;
    if (coarse) {
      // Touch: visual-only preview during the gesture, commit geometry on release.
      // Keeps per-frame React/raster work off the device so the mobile GPU process
      // stays alive (this is what was crashing Android).
      setPreview({ id: pend.id, t: pend.t });
    } else if (pend.result) {
      // Desktop: commit live every frame so the duplicate mirror and the
      // X / Y / W / H fields track in real time. No crash risk on desktop, and the
      // heavy template is memoized so this no longer churns the canvas.
      update(pend.id, pend.result);
    }
    setSizing(pend.sz);
  };
  const scheduleMove = () => {
    if (moveRafRef.current) return;
    moveRafRef.current = requestAnimationFrame(flushMove);
  };

  const onMove = (e) => {
    if (gfxPinchRef.current) return;
    const op = opRef.current;
    if (!op) return;
    const g = graphicsRef.current.find((x) => x.id === op.id);
    if (!g) return;
    const p = toSvgPoint(e.clientX, e.clientY);
    let result = null, t = null, sz = null;
    if (op.type === "drag") {
      const ncx = p.x - op.offX, ncy = p.y - op.offY;
      result = { cx: ncx, cy: ncy };
      t = `translate(${ncx - g.cx} ${ncy - g.cy})`;
    } else if (op.type === "resize") {
      const local = rotatePoint(p.x, p.y, g.cx, g.cy, g.rotation);
      let nw, nh;
      if (uniformRef.current) {
        const dist = Math.hypot(local.x - g.cx, local.y - g.cy);
        const scale = dist / op.startDist;
        nw = Math.max(16, op.startW * scale);
        nh = Math.max(16, op.startH * scale);
      } else {
        nw = Math.max(16, Math.abs(local.x - g.cx) * 2);
        nh = Math.max(16, Math.abs(local.y - g.cy) * 2);
      }
      result = { w: nw, h: nh };
      t = scaleT(g, nw / op.startW, nh / op.startH);
      sz = { x: e.clientX, y: e.clientY, w: nw, h: nh };
    } else if (op.type === "resizeV") {
      // Bottom-center handle: vertical drag. Uniform on -> scale both axes proportionally,
      // uniform off -> vertical (Y-axis) only.
      const local = rotatePoint(p.x, p.y, g.cx, g.cy, g.rotation);
      const h = Math.max(16, Math.abs(local.y - g.cy) * 2);
      let nw = g.w;
      if (uniformRef.current) {
        const scale = h / (op.startH || h);
        nw = Math.max(16, op.startW * scale);
        result = { w: nw, h };
      } else {
        result = { h };
      }
      t = scaleT(g, nw / op.startW, h / op.startH);
      sz = { x: e.clientX, y: e.clientY, w: nw, h };
    } else if (op.type === "rotate") {
      const deg = (Math.atan2(p.y - g.cy, p.x - g.cx) * 180) / Math.PI + 90;
      const r = snapRotation(deg);
      result = { rotation: r };
      t = `rotate(${r - g.rotation} ${g.cx} ${g.cy})`;
    }
    op.result = result;
    movePendingRef.current = { id: op.id, t, sz, result };
    scheduleMove();
  };

  const onUp = () => {
    if (moveRafRef.current) { cancelAnimationFrame(moveRafRef.current); moveRafRef.current = 0; }
    const op = opRef.current;
    if (op) opEndedAtRef.current = Date.now();
    if (op && op.result) update(op.id, op.result);
    // Snap-back: if a dragged graphic ended up fully outside its part, ease it back to
    // the part center. Any overlap (bleed) is allowed and left untouched.
    if (op && op.type === "drag" && op.result) {
      const base = graphicsRef.current.find((x) => x.id === op.id);
      const box = base && bboxRef.current[base.partId];
      if (base && box) {
        const finalG = { ...base, cx: op.result.cx, cy: op.result.cy };
        if (isFullyOutside(finalG, box)) {
          animateCenter(op.id, box.x + box.width / 2, box.y + box.height / 2);
        }
      }
    }
    opRef.current = null;
    movePendingRef.current = null;
    setPreview(null);
    setSizing(null);
    detachWindow();
  };

  // Always target the exact listeners we attached (closures can differ across
  // renders) and guarantee cleanup on pointercancel, which Android fires often.
  const detachWindow = () => {
    const h = activeListenersRef.current;
    if (!h) return;
    window.removeEventListener("pointermove", h.move);
    window.removeEventListener("pointerup", h.up);
    window.removeEventListener("pointercancel", h.up);
    document.removeEventListener("selectstart", blockNative);
    document.removeEventListener("dragstart", blockNative);
    activeListenersRef.current = null;
  };

  const attachWindow = () => {
    detachWindow();
    const h = { move: onMove, up: onUp };
    activeListenersRef.current = h;
    window.addEventListener("pointermove", h.move);
    window.addEventListener("pointerup", h.up);
    window.addEventListener("pointercancel", h.up);
    document.addEventListener("selectstart", blockNative);
    document.addEventListener("dragstart", blockNative);
    clearSelection();
  };

  const handleDrop = (e) => {
    if (!editable) return;
    e.preventDefault();
    let payload;
    try {
      payload = JSON.parse(e.dataTransfer.getData("application/json"));
    } catch (_) {
      return;
    }
    const partId = partUnderPoint(e.clientX, e.clientY) || activePartId;
    if (!partId) return;
    if (lockedPartIds.includes(partId)) return;
    const box = bboxRef.current[partId];
    const size = box ? Math.min(box.width, box.height) : 140;
    const g = {
      id: crypto.randomUUID(),
      fileId: payload.fileId,
      url: payload.url,
      filename: payload.filename,
      partId,
      cx: box ? box.x + box.width / 2 : 400,
      cy: box ? box.y + box.height / 2 : 300,
      originX: box ? box.x + box.width / 2 : 400,
      originY: box ? box.y + box.height / 2 : 300,
      w: size,
      h: size,
      rotation: 0,
      opacity: 1,
    };
    onChange([...graphicsRef.current, g]);
    setSelectedId && setSelectedId(g.id);
    setActivePartId && setActivePartId(partId);
    fitToPart(g.id, payload.url, partId);
  };

  const handleCanvasClick = (e) => {
    if (!editable) return;
    if (panMovedRef.current) { panMovedRef.current = false; return; }
    if (Date.now() - opEndedAtRef.current < 350) return; // click synthesized by a handle release
    const partId = partUnderPoint(e.clientX, e.clientY);
    if (partId && setActivePartId) setActivePartId(partId);
    setSelectedId && setSelectedId(null);
  };

  const selected = editable ? graphics.find((g) => g.id === selectedId) : null;
  const hs = coarse ? 1.9 : 1; // enlarge interaction handles on touch devices

  const Tx = metrics.ox * (1 - zoom) - pan.x * metrics.k * zoom;
  const Ty = metrics.oy * (1 - zoom) - pan.y * metrics.k * zoom;
  const viewTransform = `translate(${Tx}px, ${Ty}px) scale(${zoom})`;

  // Memoize the heavy static framework/measurement/guides so React bails out of
  // reconciling these subtrees on every pan/drag re-render. Without this, each
  // per-frame re-render re-processed the 200KB+ template SVG and churned tens of
  // thousands of transient DOM nodes — the real cause of the mobile GPU crash.
  const frameworkLayer = useMemo(() => <g dangerouslySetInnerHTML={{ __html: inner }} />, [inner]);
  const measurementLayer = useMemo(
    () => (measurement ? <g ref={measureRef} style={{ opacity: 0, pointerEvents: "none" }} aria-hidden dangerouslySetInnerHTML={{ __html: measurement }} data-testid="studio-measurement-ref" /> : null),
    [measurement]
  );
  const guidesLayer = useMemo(
    () => (guides ? <g ref={guideRef} style={{ pointerEvents: "none" }} dangerouslySetInnerHTML={{ __html: guides }} data-testid="studio-guide-overlay" /> : null),
    [guides]
  );

  return (
    <>
    <div
      ref={viewportRef}
      onPointerDown={onCanvasPointerDown}
      onClick={handleCanvasClick}
      onDrop={handleDrop}
      onDragOver={editable ? (e) => e.preventDefault() : undefined}
      data-testid="studio-pan-surface"
      onDragStart={blockNative}
      style={{ width: "100%", height: "100%", overflow: "hidden", touchAction: "none", overscrollBehavior: "contain", cursor: "grab", ...NO_SELECT }}
    >
    <div ref={wrapperRef} style={{ position: "relative", width: "100%", height: "100%", transform: viewTransform, transformOrigin: "0 0", willChange: "transform" }}>
    {/* STATIC framework layer — rasterized once, untouched by select/drag/resize */}
    <svg
      ref={svgRef}
      viewBox={`${vb.x} ${vb.y} ${vb.w} ${vb.h}`}
      className={className}
      data-testid={dataTestid}
      preserveAspectRatio="xMidYMid meet"
      style={{ position: "absolute", inset: 0, width: "100%", height: "100%", display: "block" }}
    >
      <defs ref={defsRef} />
      {frameworkLayer}

      {measurementLayer}

      {Object.entries(partColors || {}).map(([pid, color]) => (
        color ? (
          <rect key={pid} x={vb.x} y={vb.y} width={vb.w} height={vb.h} fill={color} clipPath={`url(#clip-${pid})`} pointerEvents="none" data-testid={`studio-part-fill-${pid}`} />
        ) : null
      ))}
    </svg>

    {/* DYNAMIC overlay layer — graphics + selection handles only. Separate SVG so
        moving/selecting a graphic repaints ONLY this light layer and never the heavy
        framework (which was exhausting the mobile GPU and crashing the renderer). */}
    <svg
      viewBox={`${vb.x} ${vb.y} ${vb.w} ${vb.h}`}
      preserveAspectRatio="xMidYMid meet"
      aria-hidden
      style={{ position: "absolute", inset: 0, width: "100%", height: "100%", display: "block", pointerEvents: "none", touchAction: "none" }}
    >
      {graphics.map((g) => {
        const pv = preview && preview.id === g.id ? preview.t : null;
        return (
        <g key={g.id} clipPath={`url(#clip-${g.partId})`} opacity={g.opacity ?? 1}>
          <g transform={pv || undefined} style={{ willChange: "transform" }}>
          <image
            href={g.url}
            x={g.cx - g.w / 2}
            y={g.cy - g.h / 2}
            width={g.w}
            height={g.h}
            transform={`rotate(${g.rotation} ${g.cx} ${g.cy})`}
            preserveAspectRatio="none"
            className="no-native-drag"
            style={{ cursor: editable && !g.dup ? "move" : "default", pointerEvents: editable && !g.dup ? "auto" : "none" }}
            data-testid={g.dup ? "studio-canvas-graphic-mirror" : "studio-canvas-graphic"}
            onPointerDown={editable && !g.dup ? (e) => startDrag(e, g) : undefined}
            onClick={editable && !g.dup ? (e) => { e.stopPropagation(); setSelectedId && setSelectedId(g.id); } : undefined}
          />
          </g>
        </g>
        );
      })}
    </svg>

    {/* TOP guide layer — must paint ABOVE the graphics so cut/stitch guides stay
        visible over placed artwork. Static + memoized (no churn); pointer-transparent
        so selection/resize/rotate handles below still receive events. */}
    <svg
      viewBox={`${vb.x} ${vb.y} ${vb.w} ${vb.h}`}
      preserveAspectRatio="xMidYMid meet"
      aria-hidden
      style={{ position: "absolute", inset: 0, width: "100%", height: "100%", display: "block", pointerEvents: "none" }}
    >
      {guidesLayer}
    </svg>

    {/* SELECTION layer — the bounding box + resize/rotate anchors must sit ABOVE
        everything (incl. the guide overlay) so the handles are never hidden. */}
    <svg
      viewBox={`${vb.x} ${vb.y} ${vb.w} ${vb.h}`}
      preserveAspectRatio="xMidYMid meet"
      aria-hidden
      style={{ position: "absolute", inset: 0, width: "100%", height: "100%", display: "block", pointerEvents: "none", touchAction: "none" }}
    >
      {selected && (
        <g transform={preview && preview.id === selected.id ? preview.t : undefined}>
        <g transform={`rotate(${selected.rotation} ${selected.cx} ${selected.cy})`}>          <rect
            x={selected.cx - selected.w / 2}
            y={selected.cy - selected.h / 2}
            width={selected.w}
            height={selected.h}
            fill="none"
            stroke="#C85A32"
            strokeWidth="1.5"
            strokeDasharray="6 4"
          />
          {[
            [selected.cx - selected.w / 2, selected.cy - selected.h / 2],
            [selected.cx + selected.w / 2, selected.cy - selected.h / 2],
            [selected.cx - selected.w / 2, selected.cy + selected.h / 2],
            [selected.cx + selected.w / 2, selected.cy + selected.h / 2],
          ].map(([hx, hy], i) => (
            <circle
              key={i}
              cx={hx}
              cy={hy}
              r={7 * hs}
              fill="#fff"
              stroke="#C85A32"
              strokeWidth="2"
              style={{ cursor: "nwse-resize", pointerEvents: "auto" }}
              onPointerDown={(e) => startResize(e, selected, i)}
              data-testid="studio-resize-handle"
            />
          ))}
          <rect
            x={selected.cx - 8 * hs}
            y={selected.cy + selected.h / 2 - 4 * hs}
            width={16 * hs}
            height={8 * hs}
            rx="2"
            fill="#fff"
            stroke="#C85A32"
            strokeWidth="2"
            style={{ cursor: "ns-resize", pointerEvents: "auto" }}
            onPointerDown={(e) => startResizeVertical(e, selected)}
            data-testid="studio-resize-handle-bottom"
          />
          <line
            x1={selected.cx}
            y1={selected.cy - selected.h / 2}
            x2={selected.cx}
            y2={selected.cy - selected.h / 2 - 30}
            stroke="#C85A32"
            strokeWidth="1.5"
          />
          <circle
            cx={selected.cx}
            cy={selected.cy - selected.h / 2 - 30}
            r="11"
            fill="#fff"
            stroke="#C85A32"
            strokeWidth="2"
          />
          <g
            transform={`translate(${selected.cx - 7}, ${selected.cy - selected.h / 2 - 30 - 7}) scale(0.583)`}
            style={{ pointerEvents: "none" }}
            stroke="#C85A32"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          >
            {/* lucide "rotate-cw" glyph */}
            <path d="M21 12a9 9 0 1 1-2.64-6.36" />
            <polyline points="21 3 21 9 15 9" />
          </g>
          <circle
            cx={selected.cx}
            cy={selected.cy - selected.h / 2 - 30}
            r={16 * hs}
            fill="transparent"
            style={{ cursor: "grab", pointerEvents: "auto" }}
            onPointerDown={(e) => startRotate(e, selected)}
            onPointerEnter={(e) => (e.currentTarget.style.cursor = "grab")}
            onPointerDownCapture={(e) => (e.currentTarget.style.cursor = "grabbing")}
            data-testid="studio-rotate-handle"
          />
        </g>
        </g>
      )}
    </svg>
    </div>
    </div>
    {sizing ? createPortal(
      <div
        style={{ position: "fixed", left: sizing.x + 16, top: sizing.y + 18, zIndex: 60, pointerEvents: "none" }}
        className="rounded-md bg-[#161616] text-[#F8F6F0] border border-[#C85A32] shadow-lg px-2.5 py-1.5 text-xs font-mono tracking-wide"
        data-testid="studio-dimension-overlay"
      >
        {upi ? fmtInch((sizing.w / upi) * (unit === "cm" ? 2.54 : 1)) : fmtInch(sizing.w)} <span className="text-[#C85A32]">×</span> {upi ? fmtInch((sizing.h / upi) * (unit === "cm" ? 2.54 : 1)) : fmtInch(sizing.h)} <span className="text-[#8C867B]">{upi ? unit : "u"}</span>
      </div>,
      document.body
    ) : null}
    </>
  );
});

export default StudioCanvas;
